/**
 * Shared auth state to prevent redundant network requests during a login
 * redirect. A 401 sets the flag, every fetch wrapper short-circuits, and
 * `window.location.href` unloads the JS context. In the rare case where
 * navigation doesn't actually happen (ad-blocker rewriting hrefs, iframe
 * without top-navigation, test harness stubbing `location`), a 10s timeout
 * clears the flag so the app can recover instead of being permanently 401'd.
 */
export let isRedirectingToLogin = false;

const REDIRECT_FLAG_TIMEOUT_MS = 10_000;
let resetTimer: ReturnType<typeof setTimeout> | null = null;

export const setRedirectingToLogin = () => {
  isRedirectingToLogin = true;
  if (resetTimer) {
    clearTimeout(resetTimer);
  }
  resetTimer = setTimeout(() => {
    isRedirectingToLogin = false;
    resetTimer = null;
  }, REDIRECT_FLAG_TIMEOUT_MS);
};

/**
 * Builds `/login?returnTo=<encoded>` from the given path+search. Defaults to
 * the current URL so callers that just want "redirect me back here" can omit
 * the argument.
 */
export const buildLoginUrl = (pathAndSearch?: string): string => {
  const target = pathAndSearch ?? window.location.pathname + window.location.search;
  return `/login?returnTo=${encodeURIComponent(target)}`;
};

// Server codes that mean "this session is genuinely dead, log the user out".
// Anything else (a transient orchestrator 401, a permission rejection) stays
// a per-request error and is handled by the calling component.
const SESSION_DEATH_CODES = new Set(['SESSION_EXPIRED', 'UNAUTHENTICATED']);

/**
 * The single 401 → redirect decision point. Called from the transport layer
 * (fetcher.ts, client.ts) on every caught error so every API consumer —
 * react-query, Redux thunks, raw component fetches — funnels through here
 * before the error reaches them. If the server tagged this 401 as a session
 * death we navigate; otherwise we no-op and let the caller handle the error.
 */
export const handleAuthFailure = (error: { statusCode?: number; code?: string }): void => {
  if (isRedirectingToLogin) {
    return;
  }
  if (error.statusCode !== 401) {
    return;
  }
  if (!SESSION_DEATH_CODES.has(error.code ?? '')) {
    return;
  }
  setRedirectingToLogin();
  window.location.href = buildLoginUrl();
};
